<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import { requireCanEditCode as vRequireCanEditCode } from '@/shared/directives/requirePermission';
    import { getTRDocumentListLink, getSqlEditorLinkBySavedQuery } from '@/shared/helpers/routing';
    import { encodeIdBase64 } from '@/shared/helpers/utils';
    import { TRDocumentWithRevisions, useTRDocumentStore } from './TRDocumentStore';
    import { TRRevisionQueryTemplate } from './TRDocumentQueries';
    import { useRouter } from 'vue-router';
    import CodeEdit from '@/shared/codeedit/CodeEdit.vue';
    import Spinner from '@/shared/components/Spinner.vue';

    type Props = {
        trDocumentIdentity: string;
    };

    const props = defineProps<Props>();
    const store = useTRDocumentStore();
    const router = useRouter();

    const trDocument = ref<TRDocumentWithRevisions | undefined>();
    const selectedQueryTemplateForEdit = ref<TRRevisionQueryTemplate | undefined>();
    const modeNewCode = ref<boolean>(false);

    const permissionSpecification = {
        libraryName: 'TRRevisionQueryTemplate'
    };

    const showCodeEdit = computed(() =>
        modeNewCode.value
        || selectedQueryTemplateForEdit.value !== undefined);

    const editCodeId = computed(() =>
        selectedQueryTemplateForEdit.value
            ? encodeIdBase64('Code', selectedQueryTemplateForEdit.value.id)
            : undefined);

    const newCodeTemplate = computed(() => ({
        TRDocument: trDocument.value?.document.identity ?? null
    }));

    watch(() => props.trDocumentIdentity, async () => {
        await store.init();
        trDocument.value = store.getTRDocument(props.trDocumentIdentity);
        await loadQueryTemplates();
    }, { immediate: true });

    async function loadQueryTemplates(): Promise<void> {
        if (trDocument.value !== undefined) {
            await store.loadQueryTemplates(trDocument.value.document);
        }
    }

    async function afterSave(): Promise<void> {
        modeNewCode.value = false;
        selectedQueryTemplateForEdit.value = undefined;
        await loadQueryTemplates();
    }

    function onEditQueryTemplate(queryTemplate: TRRevisionQueryTemplate): void {
        modeNewCode.value = false;
        selectedQueryTemplateForEdit.value = queryTemplate;
    }

    function onNewQueryTemplate(): void {
        selectedQueryTemplateForEdit.value = undefined;
        modeNewCode.value = true;
    }

    function onEditCancel(): void {
        modeNewCode.value = false;
        selectedQueryTemplateForEdit.value = undefined;
    }

</script>

<template>
    <spinner :loading="store.initializing">
        <div
            v-if="trDocument"
            class="mt-5">
            <div class="is-clearfix block">
                <div class="is-pulled-right buttons">
                    <b-button
                        icon-left="chevron-left"
                        @click="router.push(getTRDocumentListLink())">
                        Back
                    </b-button>
                    <b-button
                        v-show="!modeNewCode"
                        v-require-can-edit-code="permissionSpecification"
                        type="is-primary"
                        icon-left="plus-circle-outline"
                        @click="onNewQueryTemplate()">
                        Create new template
                    </b-button>
                </div>
                <h3 class="title is-4">
                    {{ trDocument.document.name }} / {{ trDocument.document.technologyDescription }} / {{ trDocument.document.validityContextDescription }}
                </h3>
            </div>
            <div class="columns">
                <div class="column">
                    <div class="trdocument-section-header block">
                        Query templates
                    </div>
                    <b-table
                        v-model:selected="selectedQueryTemplateForEdit"
                        :data="store.queryTemplates"
                        :loading="store.loadingQueryTemplates"
                        striped
                        focusable>
                        <b-table-column
                            v-slot="tableProps"
                            field="sequenceNo"
                            label="Seq."
                            sortable>
                            {{ tableProps.row.sequenceNo }}
                        </b-table-column>
                        <b-table-column
                            v-slot="tableProps"
                            field="name"
                            label="Name"
                            sortable>
                            {{ tableProps.row.name }}
                        </b-table-column>
                        <b-table-column
                            v-slot="tableProps"
                            :visible="!showCodeEdit"
                            field="trReference"
                            label="TR Reference"
                            sortable>
                            {{ tableProps.row.trReference }}
                        </b-table-column>
                        <b-table-column
                            v-slot="tableProps"
                            :visible="!showCodeEdit"
                            field="linkText"
                            label="Link text"
                            sortable>
                            {{ tableProps.row.linkText }}
                        </b-table-column>
                        <b-table-column
                            v-slot="tableProps"
                            :visible="!showCodeEdit"
                            field="queryId"
                            label="Query"
                            sortable>
                            <router-link :to="getSqlEditorLinkBySavedQuery(tableProps.row.queryId)">
                                {{ tableProps.row.queryId }}
                            </router-link>
                        </b-table-column>
                        <b-table-column
                            v-slot="tableProps"
                            :visible="!showCodeEdit"
                            field="id"
                            label="&nbsp;">
                            <b-button
                                v-require-can-edit-code="permissionSpecification"
                                type="is-primary"
                                icon-left="pencil"
                                outlined
                                @click="onEditQueryTemplate(tableProps.row)">
                                Edit template
                            </b-button>
                        </b-table-column>
                    </b-table>
                </div>
                <div
                    v-if="showCodeEdit"
                    class="column">
                    <code-edit
                        :key="editCodeId"
                        use-box-render
                        library="TRRevisionQueryTemplate"
                        :code-id="editCodeId"
                        :code-set-name="store.trRevisionQueryTemplateCodeSetName"
                        :code-template-values="newCodeTemplate"
                        :quick-commit="true"
                        :fields-to-disable="['TRDocument']"
                        :fields-to-ignore="['description', 'SQLQuery']"
                        @cancel="onEditCancel()"
                        @refresh="afterSave()" />
                </div>
            </div>
        </div>
    </spinner>
</template>
